import React, { useState, useEffect } from 'react';

function MoveDialog({ onClose, onMove, classOptions, student, selectedClass }) {
  const [newClass, setNewClass] = useState(selectedClass);
  const [newDate, setNewDate] = useState('');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-md w-full">
        <h3 className="text-lg font-bold mb-4">Move Student</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Select New Class:</label>
            <select
              className="w-full p-2 border rounded"
              value={newClass}
              onChange={(e) => {
                setNewClass(e.target.value);
                setNewDate('');
              }}
            >
              <option value="">Select a class...</option>
              {Object.keys(classOptions).map(className => (
                <option key={className} value={className}>{className}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Select New Date:</label>
            <select
              className="w-full p-2 border rounded"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              disabled={!newClass}
            >
              <option value="">Select a date...</option>
              {newClass && classOptions[newClass]?.dates.map(date => (
                <option key={date} value={date}>{date}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button 
              type="button"
              onClick={onClose} 
              className="px-4 py-2 border rounded hover:bg-gray-100"
            >
              Cancel
            </button>
            <button 
              type="button"
              onClick={() => onMove(newClass, newDate)}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              disabled={!newClass || !newDate}
            >
              Move
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StudentForm({ student, index, updateStudent, onDelete, onMove, selectedClass, selectedDate, certificationOptions, calculateFees }) {
  return (
    <div className="border p-4 mb-6 rounded-lg bg-gray-50">
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-bold">STUDENT #{index + 1}</h4>
        <div className="text-sm text-gray-600">
          <p>Class: {student.class || selectedClass}</p>
          <p>Date: {student.date || selectedDate}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium mb-1">First Name:</label>
          <input
            type="text"
            value={student.firstName || ''}
            onChange={(e) => updateStudent(student.id, 'firstName', e.target.value)}
            className="w-full p-2 border rounded"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Last Name:</label>
          <input
            type="text"
            value={student.lastName || ''}
            onChange={(e) => updateStudent(student.id, 'lastName', e.target.value)}
            className="w-full p-2 border rounded"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium mb-1">Government ID Type:</label>
          <input
            type="text"
            placeholder="e.g., TXDL"
            value={student.idType || ''}
            onChange={(e) => updateStudent(student.id, 'idType', e.target.value)}
            className="w-full p-2 border rounded"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ID Number:</label>
          <input
            type="text"
            value={student.idNumber || ''}
            onChange={(e) => updateStudent(student.id, 'idNumber', e.target.value)}
            className="w-full p-2 border rounded"
            required
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Additional Certifications:</label>
        <div className="grid grid-cols-2 gap-2">
          {certificationOptions.map((cert, certIndex) => (
            <label key={certIndex} className="flex items-center">
              <input
                type="checkbox"
                checked={(student.certifications || []).includes(cert.name)}
                onChange={(e) => {
                  const newCerts = e.target.checked
                    ? [...(student.certifications || []), cert.name]
                    : (student.certifications || []).filter(c => c !== cert.name);
                  updateStudent(student.id, 'certifications', newCerts);
                }}
                className="mr-2"
              />
              {cert.name} (+${cert.price})
            </label>
          ))}
        </div>
      </div>

      <div className="flex justify-between items-center mt-4">
        <div className="text-lg font-semibold">
          Total Fees: ${calculateFees(student).toFixed(2)}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onMove(student)}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Move Student
          </button>
          <button
            type="button"
            onClick={() => onDelete(student.id)}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Delete Student
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RegistrationForm() {
  const classOptions = {
    'NFPA70E – LEVEL 3 FULL CLASS': {
      price: 350.00,
      dates: [
        'JAN 20, 2025', 'FEB 17, 2025', 'MAR 17, 2025', 'APR 21, 2025',
        'MAY 19, 2025', 'JUN 23, 2025', 'JUL 21, 2025', 'AUG 25, 2025',
        'SEP 22, 2025', 'OCT 20, 2025', 'NOV 17, 2025', 'DEC 15, 2025'
      ]
    },
    'NFPA70E – LEVEL 2 TECH CLASS': {
      price: 300.00,
      dates: [
        'JAN 21, 2025', 'FEB 18, 2025', 'MAR 18, 2025', 'APR 22, 2025',
        'MAY 20, 2025', 'JUN 24, 2025', 'JUL 22, 2025', 'AUG 26, 2025',
        'SEP 23, 2025', 'OCT 21, 2025', 'NOV 18, 2025', 'DEC 16, 2025'
      ]
    },
    'NFPA70E – REFRESHER CLASS': {
      price: 250.00,
      dates: [
        'JAN 22, 2025', 'FEB 19, 2025', 'MAR 19, 2025', 'APR 23, 2025',
        'MAY 21, 2025', 'JUN 25, 2025', 'JUL 23, 2025', 'AUG 27, 2025',
        'SEP 24, 2025', 'OCT 22, 2025', 'NOV 19, 2025', 'DEC 17, 2025'
      ]
    },
    'NFPA70E – POWERLINE SAFETY': {
      price: 375.00,
      dates: [
        'JAN 23, 2025', 'FEB 20, 2025', 'MAR 20, 2025', 'APR 24, 2025',
        'MAY 22, 2025', 'JUN 26, 2025', 'JUL 24, 2025', 'AUG 28, 2025',
        'SEP 25, 2025', 'OCT 23, 2025', 'NOV 20, 2025', 'DEC 18, 2025'
      ]
    }
  };

  const certificationOptions = [
    { name: 'Up to 5000 volts', price: 25 },
    { name: 'Up to 15000 volts', price: 25 },
    { name: 'Up to 36000 volts', price: 25 },
    { name: 'High Voltage Meter w/Hot Sticks', price: 25 },
    { name: 'Hot Stick Use', price: 25 },
    { name: 'Digger Operation', price: 25 },
    { name: 'Pressure Digger', price: 25 },
    { name: 'Bucket Operation w/Rescue', price: 50 }
  ];

  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [students, setStudents] = useState([]);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [studentToMove, setStudentToMove] = useState(null);
  const [totalFees, setTotalFees] = useState(0);
  const [companyInfo, setCompanyInfo] = useState({
    firstName: '', lastName: '', company: '', address: '', 
    city: '', state: '', zip: '', email: '', phone: '', cell: ''
  });
  const [billingInfo, setBillingInfo] = useState({
    firstName: '', lastName: '', company: '', address: '', 
    city: '', state: '', zip: '', email: '', phone: '', cell: ''
  });

  useEffect(() => {
    if (students.length === 0) {
      addStudent();
    }
  }, []);

  useEffect(() => {
    const total = students.reduce((sum, student) => sum + calculateStudentFees(student), 0);
    setTotalFees(total);
  }, [students, selectedClass]);

  const calculateStudentFees = (student) => {
    let total = 0;
    const studentClass = student.class || selectedClass;
    if (studentClass && classOptions[studentClass]) {
      total += classOptions[studentClass].price;
    }
    if (student.certifications && student.certifications.length > 0) {
      student.certifications.forEach(cert => {
        const certification = certificationOptions.find(c => c.name === cert);
        if (certification) {
          total += certification.price;
        }
      });
    }
    return total;
  };

  const addStudent = () => {
    const newStudent = {
      id: Date.now(),
      firstName: '',
      lastName: '',
      idType: '',
      idNumber: '',
      certifications: [],
      class: selectedClass,
      date: selectedDate
    };
    setStudents([...students, newStudent]);
  };

  const updateStudent = (id, field, value) => {
    setStudents(students.map(student =>
      student.id === id ? { ...student, [field]: value } : student
    ));
  };

  const deleteStudent = (id) => {
    if (students.length > 1) {
      setStudents(students.filter(s => s.id !== id));
    }
  };

  const handleMoveStudent = (newClass, newDate) => {
    if (studentToMove && newClass && newDate) {
      setStudents(students.map(student =>
        student.id === studentToMove.id
          ? { ...student, class: newClass, date: newDate }
          : student
      ));
      setStudentToMove(null);
      setShowMoveDialog(false);
    }
  };

  const updateCompanyInfo = (field, value) => {
    setCompanyInfo(prev => ({ ...prev, [field]: value }));
  };

  const updateBillingInfo = (field, value) => {
    setBillingInfo(prev => ({ ...prev, [field]: value }));
  };

  const formatEmailContent = () => {
    const content = `
REGISTRATION FORM - NFPA 70E Training

CLASS INFORMATION
----------------
Selected Class: ${selectedClass}
Class Date: ${selectedDate}
Base Price: $${selectedClass ? classOptions[selectedClass].price.toFixed(2) : '0.00'}

COMPANY INFORMATION
-----------------
Contact: ${companyInfo.firstName} ${companyInfo.lastName}
Company: ${companyInfo.company}
Address: ${companyInfo.address}
         ${companyInfo.city}, ${companyInfo.state} ${companyInfo.zip}
Email: ${companyInfo.email}
Phone: ${companyInfo.phone}
Cell: ${companyInfo.cell}

BILLING INFORMATION
-----------------
Contact: ${billingInfo.firstName} ${billingInfo.lastName}
Company: ${billingInfo.company}
Address: ${billingInfo.address}
         ${billingInfo.city}, ${billingInfo.state} ${billingInfo.zip}
Email: ${billingInfo.email}
Phone: ${billingInfo.phone}
Cell: ${billingInfo.cell}

STUDENT INFORMATION
-----------------
${students.map((student, index) => `
Student #${index + 1}:
Name: ${student.firstName} ${student.lastName}
Class: ${student.class || selectedClass}
Date: ${student.date || selectedDate}
Additional Certifications: ${student.certifications && student.certifications.length > 0 
  ? '\n  - ' + student.certifications.join('\n  - ')
  : 'None'}
Student Fee: $${calculateStudentFees(student).toFixed(2)}
`).join('\n')}

TOTAL REGISTRATION FEE: $${totalFees.toFixed(2)}
`;
    return content;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Submit button clicked!");

    try {
      // Structure the data for Google Sheets
      const formData = {
        timestamp: new Date().toISOString(),
        className: selectedClass,
        classDate: selectedDate,
        classPrice: selectedClass ? classOptions[selectedClass].price : 0,
        
        // Company info
        companyName: companyInfo.company,
        contactFirstName: companyInfo.firstName,
        contactLastName: companyInfo.lastName,
        companyEmail: companyInfo.email,
        companyPhone: companyInfo.phone,
        companyAddress: `${companyInfo.address}, ${companyInfo.city}, ${companyInfo.state} ${companyInfo.zip}`,
        
        // Billing info
        billingContactName: `${billingInfo.firstName} ${billingInfo.lastName}`,
        billingCompany: billingInfo.company,
        billingEmail: billingInfo.email,
        billingPhone: billingInfo.phone,
        billingAddress: `${billingInfo.address}, ${billingInfo.city}, ${billingInfo.state} ${billingInfo.zip}`,
        
        // Student info
        studentCount: students.length,
        totalFees: totalFees,
        
        // Each student will be sent as a separate row
        students: students.map(student => ({
          firstName: student.firstName,
          lastName: student.lastName,
          idType: student.idType || '',
          idNumber: student.idNumber || '',
          email: student.email || '',
          className: student.class || selectedClass,
          classDate: student.date || selectedDate,
          certifications: (student.certifications || []).join(', '),
          fee: calculateStudentFees(student)
        }))
      };

      // Google Apps Script Web App URL
      const scriptURL = 'https://script.google.com/macros/s/AKfycbz3qlHuul5-i22XNsLykezk0g7f4cT5QdKV2UEg7PdA4aaOT0v659MnsG9bVPCJkCFhpg/exec';
      
      console.log('Submitting to Google Sheets:', formData);

      try {
        // Send the data to Google Sheets
        const response = await fetch(scriptURL, {
          method: 'POST',
          mode: 'no-cors', // Try adding this to avoid CORS issues
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        });
        
        console.log('Response from Google Sheets:', response);
        
        // Show success message
        alert('Registration submitted successfully! Your information has been recorded.');
      } catch (fetchError) {
        console.error('Fetch error:', fetchError);
        alert('Error connecting to Google Sheets. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      alert('There was an error submitting your registration. Please check the form and try again.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold">REGISTRATION FORM</h1>
        <h2 className="text-xl">Open Enrollment</h2>
        <h3 className="text-lg">NFPA 70E Training</h3>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Class Selection */}
        <div className="mb-6">
          <h3 className="text-lg font-bold mb-4 bg-gray-200 p-2">CLASS INFORMATION</h3>
          <div className="grid grid-cols-1 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Select Class:</label>
              <select 
                value={selectedClass}
                onChange={(e) => {
                  setSelectedClass(e.target.value);
                  setSelectedDate('');
                }}
                className="w-full p-2 border rounded"
                required
              >
                <option value="">Select a class...</option>
                {Object.keys(classOptions).map((className) => (
                  <option key={className} value={className}>
                    {className} (${classOptions[className].price})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Class Date:</label>
                <select
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full p-2 border rounded"
                  required
                  disabled={!selectedClass}
                >
                  <option value="">Select a date...</option>
                  {selectedClass && classOptions[selectedClass].dates.map((date) => (
                    <option key={date} value={date}>{date}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Class Price:</label>
                <input 
                  type="text" 
                  value={`$${selectedClass ? classOptions[selectedClass].price.toFixed(2) : '0.00'}`}
                  className="w-full p-2 border rounded bg-gray-100"
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className="mb-6">
          <h3 className="text-lg font-bold mb-4 bg-gray-200 p-2">COMPANY INFORMATION</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Contact First Name:</label>
              <input 
                type="text"
                value={companyInfo.firstName}
                onChange={(e) => updateCompanyInfo('firstName', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contact Last Name:</label>
              <input 
                type="text"
                value={companyInfo.lastName}
                onChange={(e) => updateCompanyInfo('lastName', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Company:</label>
              <input 
                type="text"
                value={companyInfo.company}
                onChange={(e) => updateCompanyInfo('company', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Address:</label>
              <input 
                type="text"
                value={companyInfo.address}
                onChange={(e) => updateCompanyInfo('address', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">City:</label>
              <input 
                type="text"
                value={companyInfo.city}
                onChange={(e) => updateCompanyInfo('city', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">State:</label>
              <input 
                type="text"
                value={companyInfo.state}
                onChange={(e) => updateCompanyInfo('state', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Zip:</label>
              <input 
                type="text"
                value={companyInfo.zip}
                onChange={(e) => updateCompanyInfo('zip', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email:</label>
              <input 
                type="email"
                value={companyInfo.email}
                onChange={(e) => updateCompanyInfo('email', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone:</label>
              <input 
                type="tel"
                value={companyInfo.phone}
                onChange={(e) => updateCompanyInfo('phone', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cell:</label>
              <input 
                type="tel"
                value={companyInfo.cell}
                onChange={(e) => updateCompanyInfo('cell', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
          </div>
        </div>

        {/* Billing Information */}
        <div className="mb-6">
          <h3 className="text-lg font-bold mb-4 bg-gray-200 p-2">BILLING INFORMATION</h3>
          <p className="text-sm mb-4 italic">Complete billing information is required for each registration</p>
          <div className="flex justify-end mb-4">
            <button
              type="button"
              onClick={() => setBillingInfo(companyInfo)}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Copy from Company Information
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Contact First Name:</label>
              <input 
                type="text"
                value={billingInfo.firstName}
                onChange={(e) => updateBillingInfo('firstName', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contact Last Name:</label>
              <input 
                type="text"
                value={billingInfo.lastName}
                onChange={(e) => updateBillingInfo('lastName', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Company:</label>
              <input 
                type="text"
                value={billingInfo.company}
                onChange={(e) => updateBillingInfo('company', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Address:</label>
              <input 
                type="text"
                value={billingInfo.address}
                onChange={(e) => updateBillingInfo('address', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">City:</label>
              <input 
                type="text"
                value={billingInfo.city}
                onChange={(e) => updateBillingInfo('city', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">State:</label>
              <input 
                type="text"
                value={billingInfo.state}
                onChange={(e) => updateBillingInfo('state', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Zip:</label>
              <input 
                type="text"
                value={billingInfo.zip}
                onChange={(e) => updateBillingInfo('zip', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email:</label>
              <input 
                type="email"
                value={billingInfo.email}
                onChange={(e) => updateBillingInfo('email', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone:</label>
              <input 
                type="tel"
                value={billingInfo.phone}
                onChange={(e) => updateBillingInfo('phone', e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cell:</label>
              <input 
                type="tel"
                value={billingInfo.cell}
                onChange={(e) => updateBillingInfo('cell', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
          </div>
        </div>

        {/* Student Information */}
        <div className="mb-6">
          <h3 className="text-lg font-bold mb-4 bg-gray-200 p-2">STUDENT INFORMATION</h3>
          {students.map((student, index) => (
            <StudentForm
              key={student.id}
              student={student}
              index={index}
              updateStudent={updateStudent}
              onDelete={deleteStudent}
              onMove={(student) => {
                setStudentToMove(student);
                setShowMoveDialog(true);
              }}
              selectedClass={selectedClass}
              selectedDate={selectedDate}
              certificationOptions={certificationOptions}
              calculateFees={calculateStudentFees}
            />
          ))}
          <div className="mt-4">
            <button
              type="button"
              onClick={addStudent}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              disabled={!selectedClass || !selectedDate}
            >
              Add Student
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-between items-center mt-8">
          <div className="text-xl font-bold">
            Total Registration Fee: ${totalFees.toFixed(2)}
          </div>
          <button 
            type="submit" 
            className="bg-blue-500 text-white px-6 py-3 rounded hover:bg-blue-600"
          >
            Submit Registration
          </button>
        </div>
      </form>

      {showMoveDialog && studentToMove && (
        <MoveDialog
          onClose={() => {
            setShowMoveDialog(false);
            setStudentToMove(null);
          }}
          onMove={handleMoveStudent}
          classOptions={classOptions}
          student={studentToMove}
          selectedClass={selectedClass}
        />
      )}
    </div>
  );
}